import { useEffect, useState } from "react";
import { Loader2, Send, Wallet, ArrowDownToLine, ExternalLink, RefreshCw, CheckCircle2, Building2, Trash2, Repeat } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
const CHIP = {
  paid: ["Sent ✓", "bg-[#2f7d5b]/15 text-[#2f7d5b]"],
  funded: ["Funded ✓", "bg-[#2f7d5b]/15 text-[#2f7d5b]"],
  processing: ["Processing", "bg-[#c9a961]/20 text-[#8a6d2f]"],
  returned: ["Returned", "bg-[#b23a48]/12 text-[#b23a48]"],
  failed: ["Failed", "bg-[#b23a48]/12 text-[#b23a48]"],
  cancelled: ["Cancelled", "bg-[#1e3d3a]/8 text-[#1e3d3a]/55"],
};

export default function PortalBillPay() {
  const [status, setStatus] = useState(null);
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selVendor, setSelVendor] = useState(null);
  const [saveVendor, setSaveVendor] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [schedForm, setSchedForm] = useState({ vendor_id: "", amount: "", cadence: "monthly", start_date: "" });
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ to_name: "", to_email: "", amount: "", description: "", routing_number: "", account_number: "", account_holder_type: "company", purpose: "vendor", note: "", category: "" });
  const [fundAmt, setFundAmt] = useState("");

  const load = async () => {
    try {
      const [{ data: s }, { data: l }, { data: v }, { data: sc }] = await Promise.all([
        api.get("/portal/billpay/status"),
        api.get("/portal/billpay"),
        api.get("/portal/vendors"),
        api.get("/portal/billpay/schedules"),
      ]);
      setStatus(s); setRows(l.payments || []); setVendors(v.vendors || []); setSchedules(sc.schedules || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const setup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/portal/billpay/setup", { origin_url: window.location.origin });
      setStatus(data);
      if (data?.onboarding_url) {
        window.open(data.onboarding_url, "_blank", "noopener");
        toast.success("Finish your payout onboarding, then we'll check automatically.");
        let n = 0; const iv = setInterval(async () => {
          n++;
          try { const { data: s } = await api.get("/portal/billpay/status"); setStatus(s); if (s.treasury_active && s.outbound_ready) { clearInterval(iv); toast.success("Bill pay is ready ✓"); } } catch { /* keep polling */ }
          if (n >= 30) clearInterval(iv);
        }, 3000);
      } else if (data?.treasury_active) {
        toast.success("Bill pay is ready.");
      } else {
        toast.message(data?.message || "Requested — Stripe will activate Treasury when approved.");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start bill-pay setup."); }
    finally { setBusy(false); }
  };

  const fund = async () => {
    if (!(Number(fundAmt) > 0)) { toast.error("Enter an amount to add."); return; }
    setBusy(true);
    try {
      await api.post("/portal/billpay/fund", { amount: Number(fundAmt) });
      setFundAmt(""); await load();
      toast.success("Pulling funds from your bank — this settles in 1-2 business days.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add funds."); }
    finally { setBusy(false); }
  };

  const pay = async () => {
    const payload = { amount: Number(form.amount), description: form.description, purpose: form.purpose };
    if (selVendor) {
      payload.vendor_id = selVendor.id;
    } else {
      if (!form.to_name.trim()) { toast.error("Enter the business name."); return; }
      if (form.routing_number.replace(/\D/g, "").length !== 9 || !form.account_number.replace(/\D/g, "")) {
        toast.error("Enter a 9-digit routing number and account number."); return;
      }
      payload.to_name = form.to_name;
      payload.to_email = form.to_email;
      payload.bank = { routing_number: form.routing_number, account_number: form.account_number, account_holder_type: form.account_holder_type };
      payload.save_vendor = saveVendor;
      if (saveVendor) { payload.note = form.note; payload.category = form.category; }
    }
    if (!(Number(form.amount) > 0)) { toast.error("Enter a valid amount."); return; }
    setBusy(true);
    try {
      await api.post("/portal/billpay", payload);
      setForm({ to_name: "", to_email: "", amount: "", description: "", routing_number: "", account_number: "", account_holder_type: "company", purpose: "vendor" });
      setSelVendor(null); setSaveVendor(false); setShowForm(false);
      await load();
      toast.success("Payment sent to the vendor's bank.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the payment."); }
    finally { setBusy(false); }
  };

  const removeVendor = async (vid) => {
    try { await api.delete(`/portal/vendors/${vid}`); if (selVendor?.id === vid) setSelVendor(null); await load(); toast.success("Vendor removed."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove vendor."); }
  };

  const openPayVendor = (v) => { setSelVendor(v); setForm((f) => ({ ...f, amount: "", description: "" })); setShowForm(true); };

  const createSchedule = async () => {
    if (!schedForm.vendor_id) { toast.error("Pick a saved vendor."); return; }
    if (!(Number(schedForm.amount) > 0)) { toast.error("Enter an amount."); return; }
    setBusy(true);
    try {
      await api.post("/portal/billpay/schedules", { vendor_id: schedForm.vendor_id, amount: Number(schedForm.amount), cadence: schedForm.cadence, start_date: schedForm.start_date || undefined });
      setSchedForm({ vendor_id: "", amount: "", cadence: "monthly", start_date: "" });
      await load();
      toast.success("Auto-pay scheduled.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not schedule auto-pay."); }
    finally { setBusy(false); }
  };

  const toggleSchedule = async (s) => {
    try { await api.patch(`/portal/billpay/schedules/${s.id}`, { enabled: !s.enabled }); await load(); }
    catch { toast.error("Could not update the schedule."); }
  };

  const removeSchedule = async (sid) => {
    try { await api.delete(`/portal/billpay/schedules/${sid}`); await load(); toast.success("Auto-pay cancelled."); }
    catch { toast.error("Could not cancel auto-pay."); }
  };

  const refresh = async (id) => { try { await api.get(`/portal/billpay/${id}/status`); await load(); } catch { /* ignore */ } };

  const ready = status?.treasury_active && status?.outbound_ready;
  const inp = "w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none";

  return (
    <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5 lg:p-6" data-testid="portal-billpay">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 text-[#1e3d3a]"><Wallet className="h-4 w-4 text-[#c9a961]" /><h3 className="font-head text-lg">Pay a business — bill pay</h3></div>
        {ready && (
          <span className="rounded-full bg-[#2f7d5b]/15 px-2.5 py-1 text-[10px] uppercase tracking-wide text-[#2f7d5b]">
            Balance {money(status?.balance || 0)}
          </span>
        )}
      </div>
      <p className="text-xs text-[#1e3d3a]/55 mb-4">Pay any vendor, supplier or contractor by bank transfer — funded straight from your connected bank account.</p>

      {!ready ? (
        <div className="rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/8 px-4 py-4" data-testid="billpay-setup-panel">
          <p className="text-sm text-[#1e3d3a] mb-3">{status?.message || "Set up bill pay to start funding and paying vendors directly from your bank."}</p>
          <button data-testid="billpay-setup" onClick={setup} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />} Set up bill pay
          </button>
        </div>
      ) : (
        <>
          {/* Fund balance */}
          <div className="flex flex-col sm:flex-row sm:items-end gap-2 mb-4 rounded-xl border border-[#1e3d3a]/10 bg-[#f7f5ef] px-4 py-3">
            <div className="flex-1">
              <label className="block text-[11px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Add funds from your bank</label>
              <input data-testid="billpay-fund-amount" className={inp} type="number" placeholder="Amount $" value={fundAmt} onChange={(e) => setFundAmt(e.target.value)} />
            </div>
            <button data-testid="billpay-fund" onClick={fund} disabled={busy}
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowDownToLine className="h-4 w-4" />} Add funds
            </button>
          </div>

          {/* Saved vendors — one-tap pay */}
          {vendors.length > 0 && (
            <div className="mb-3" data-testid="billpay-vendors">
              <p className="text-[11px] uppercase tracking-wide text-[#1e3d3a]/45 mb-1.5">Saved vendors</p>
              <div className="flex flex-wrap gap-1.5">
                {vendors.map((v) => (
                  <span key={v.id} data-testid={`vendor-${v.id}`}
                    className={`group inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${selVendor?.id === v.id ? "border-[#c9a961] bg-[#c9a961]/15 text-[#1e3d3a]" : "border-[#1e3d3a]/12 text-[#1e3d3a]/75 hover:border-[#c9a961]/60"}`}>
                    <button data-testid={`vendor-pay-${v.id}`} onClick={() => openPayVendor(v)} className="inline-flex items-center gap-1.5" title={v.note ? `${v.name} · ${v.note}` : `Pay ${v.name}`}>
                      <Building2 className="h-3.5 w-3.5 text-[#c9a961]" /> {v.name}
                      {v.category && <span className="rounded-full bg-[#1e3d3a]/6 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-[#1e3d3a]/50">{v.category}</span>}
                      <span className="text-[#1e3d3a]/40">••{v.account_last4}</span>
                    </button>
                    <button data-testid={`vendor-delete-${v.id}`} onClick={() => removeVendor(v.id)} className="text-[#1e3d3a]/30 hover:text-[#b23a48]" title="Remove vendor"><Trash2 className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Pay a vendor */}
          {!showForm ? (
            <button data-testid="billpay-new" onClick={() => { setSelVendor(null); setShowForm(true); }}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#c9a961] px-4 py-2 text-sm font-semibold text-[#1a2e2b] hover:bg-[#d8b972] mb-4">
              <Building2 className="h-4 w-4" /> Pay a business
            </button>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
              {selVendor ? (
                <div data-testid="billpay-selected-vendor" className="sm:col-span-2 flex items-center justify-between rounded-lg border border-[#c9a961]/40 bg-[#c9a961]/8 px-3 py-2 text-sm text-[#1e3d3a]">
                  <span className="inline-flex items-center gap-2"><Building2 className="h-4 w-4 text-[#c9a961]" /> Paying <strong>{selVendor.name}</strong> · ••{selVendor.account_last4}</span>
                  <button data-testid="billpay-change-vendor" onClick={() => setSelVendor(null)} className="text-xs text-[#1e3d3a]/55 hover:text-[#1e3d3a]">Change</button>
                </div>
              ) : (
                <>
                  <input data-testid="billpay-to-name" className={inp} placeholder="Business / vendor name" value={form.to_name} onChange={(e) => setForm({ ...form, to_name: e.target.value })} />
                  <input data-testid="billpay-to-email" className={inp} placeholder="Their email (optional)" value={form.to_email} onChange={(e) => setForm({ ...form, to_email: e.target.value })} />
                  <input data-testid="billpay-routing" className={inp} placeholder="Routing number (9 digits)" value={form.routing_number} onChange={(e) => setForm({ ...form, routing_number: e.target.value })} />
                  <input data-testid="billpay-account" className={inp} placeholder="Account number" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} />
                  <select data-testid="billpay-holder-type" className={inp} value={form.account_holder_type} onChange={(e) => setForm({ ...form, account_holder_type: e.target.value })}>
                    <option value="company">Business account</option>
                    <option value="individual">Individual account</option>
                  </select>
                  <label className="flex items-center gap-2 text-xs text-[#1e3d3a]/70 px-1">
                    <input data-testid="billpay-save-vendor" type="checkbox" checked={saveVendor} onChange={(e) => setSaveVendor(e.target.checked)} /> Save this vendor for next time
                  </label>
                  {saveVendor && (
                    <>
                      <input data-testid="billpay-vendor-category" className={inp} placeholder="Category (e.g. Supplies)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                      <input data-testid="billpay-vendor-note" className={inp} placeholder="Note (e.g. Net 30 terms)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                    </>
                  )}
                </>
              )}
              <input data-testid="billpay-amount" className={inp} type="number" placeholder="Amount $" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              <input data-testid="billpay-desc" className={inp} placeholder="What for" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <select data-testid="billpay-purpose" className={inp} value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })}>
                <option value="vendor">Vendor / supplier</option>
                <option value="payroll">Payroll / contractor</option>
              </select>
              <div className="sm:col-span-2 flex gap-2">
                <button data-testid="billpay-send" onClick={pay} disabled={busy}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {selVendor ? `Send to ${selVendor.name}` : "Send payment"}
                </button>
                <button data-testid="billpay-cancel" onClick={() => { setShowForm(false); setSelVendor(null); }} className="rounded-full border border-[#1e3d3a]/15 px-4 py-2 text-sm text-[#1e3d3a]/70 hover:bg-[#1e3d3a]/5">Cancel</button>
              </div>
            </div>
          )}

          {/* Recurring auto-pay */}
          {vendors.length > 0 && (
            <div className="mt-2 mb-4 rounded-xl border border-[#1e3d3a]/10 bg-[#f7f5ef] px-4 py-3" data-testid="billpay-schedules">
              <div className="flex items-center gap-2 mb-2 text-[#1e3d3a]"><Repeat className="h-4 w-4 text-[#c9a961]" /><span className="text-sm font-medium">Recurring auto-pay</span></div>
              {schedules.length > 0 && (
                <ul className="mb-3 divide-y divide-[#1e3d3a]/8">
                  {schedules.map((s) => (
                    <li key={s.id} data-testid={`schedule-${s.id}`} className="flex items-center justify-between gap-3 py-2">
                      <div className="min-w-0">
                        <div className="text-sm text-[#1e3d3a] truncate">{s.vendor_name} · {money(s.amount)} <span className="text-[#1e3d3a]/45">/ {s.cadence}</span></div>
                        <div className="text-[11px] text-[#1e3d3a]/50">Next: {s.next_run_date}{s.last_status ? ` · last: ${s.last_status}` : ""}</div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button data-testid={`schedule-toggle-${s.id}`} onClick={() => toggleSchedule(s)} className={`rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wide ${s.enabled ? "bg-[#2f7d5b]/15 text-[#2f7d5b]" : "bg-[#1e3d3a]/8 text-[#1e3d3a]/55"}`}>{s.enabled ? "On" : "Paused"}</button>
                        <button data-testid={`schedule-delete-${s.id}`} onClick={() => removeSchedule(s.id)} className="p-1 text-[#1e3d3a]/30 hover:text-[#b23a48]"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                <select data-testid="schedule-vendor" className={inp} value={schedForm.vendor_id} onChange={(e) => setSchedForm({ ...schedForm, vendor_id: e.target.value })}>
                  <option value="">Pick a vendor…</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
                <input data-testid="schedule-amount" className={inp} type="number" placeholder="Amount $" value={schedForm.amount} onChange={(e) => setSchedForm({ ...schedForm, amount: e.target.value })} />
                <select data-testid="schedule-cadence" className={inp} value={schedForm.cadence} onChange={(e) => setSchedForm({ ...schedForm, cadence: e.target.value })}>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Every 2 weeks</option>
                  <option value="monthly">Monthly</option>
                </select>
                <button data-testid="schedule-create" onClick={createSchedule} disabled={busy}
                  className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Repeat className="h-4 w-4" />} Schedule
                </button>
              </div>
            </div>
          )}
        </>
      )}
        {ready && (
        <ul className="divide-y divide-[#1e3d3a]/8" data-testid="billpay-list">
          {rows.map((r) => {
            const [label, cls] = CHIP[r.status] || CHIP.processing;
            return (
              <li key={r.id} data-testid={`billpay-${r.id}`} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#1e3d3a] truncate">
                    {r.kind === "funding" ? "↓ Balance top-up" : r.to_name || r.to_email} · {money(r.amount)}
                  </div>
                  <div className="text-[11px] text-[#1e3d3a]/50 truncate">{r.description}{r.bank_last4 ? ` · ••${r.bank_last4}` : ""}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className={`rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wide ${cls}`}>{label}</span>
                  {!["paid", "funded"].includes(r.status)
                    ? <button data-testid={`billpay-refresh-${r.id}`} onClick={() => refresh(r.id)} className="p-1 text-[#1e3d3a]/40 hover:text-[#c9a961]" title="Check status"><RefreshCw className="h-3.5 w-3.5" /></button>
                    : <CheckCircle2 className="h-4 w-4 text-[#2f7d5b]" />}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
